<template>
	<Drawer :visible="visible" @update:visible="onClose" position="right" :style="{ width: expanded ? '50vw' : '40em', maxWidth: '90vw' }">
		<template #header>
			<div class="flex items-center justify-between w-full">
				<span class="font-semibold text-base">
					{{ isEditing ? `Editing help text for ${label}` : `${label}` }}
				</span>
				<div class="flex items-center gap-2">
					<button class="header-btn" @click="expanded = !expanded" :aria-label="expanded ? 'Collapse' : 'Expand'">
						<FontAwesomeIcon :icon="['far', expanded ? 'chevrons-right' : 'chevrons-left']" />
					</button>
					<button v-if="!isEditing && isAdminUser" class="header-btn" @click="isEditing = true" aria-label="Edit help text">
						<FontAwesomeIcon :icon="['far', 'pen']" />
					</button>
				</div>
			</div>
		</template>

		<div class="flex flex-col gap-3 h-full">
			<div v-if="loading" class="flex items-center justify-center h-32">
				<i class="pi pi-spin pi-spinner" style="font-size: 2rem" />
			</div>
			<template v-else>
				<div v-if="!isEditing" class="help-content prose max-w-none" v-html="helpTextContent" />
				<Editor v-else v-model="helpTextContent" editorStyle="height: calc(100vh - 12rem)" />
			</template>
		</div>

		<template v-if="isEditing" #footer>
			<div class="flex justify-end gap-2 p-3">
				<Button label="Cancel" class="p-button-text" @click="cancelEdit" />
				<Button label="Save" class="btn p-button" @click="save" :loading="saving" />
			</div>
		</template>
	</Drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue"
import Drawer from "primevue/drawer"
import Button from "primevue/button"
import Editor from "primevue/editor"
import { useToast } from "primevue/usetoast"
import { HelpLabelClient, UpdateHelpTextRequest } from "../../../entities/NSwagGenerated"
import { useHelpLabelStore } from "@/store/helpLabelStore"
import { SessionInfo } from "@/auth/personSessionData"
import { collapseEmptyParagraphs } from "@/utils/richTextUtils"

const props = defineProps({
	visible: {
		type: Boolean,
		required: true,
	},
	helpElementName: {
		type: String,
		required: true,
	},
	label: {
		type: String,
		required: true,
	},
	readonly: {
		type: Boolean,
		required: false,
		default: false,
	},
})

const emit = defineEmits<{
	"update:visible": [value: boolean]
	saved: []
}>()

const toast = useToast()
const helpLabelStore = useHelpLabelStore()
const helpLabelClient = new HelpLabelClient(import.meta.env.VITE_API_URL?.toString())

const helpTextContent = ref("")
const loading = ref(false)
const saving = ref(false)
const isEditing = ref(false)
const expanded = ref(false)

const isAdminUser = computed(() => {
	try {
		return SessionInfo.currentUserIsAdmin() || SessionInfo.currentUser()?.role?.indexOf("Client Admin") !== -1
	} catch {
		return false
	}
})

watch(
	() => props.visible,
	async (isVisible) => {
		if (isVisible) {
			isEditing.value = !props.readonly
			loading.value = true
			try {
				helpTextContent.value = (await helpLabelClient.helptextGet(props.helpElementName)) ?? ""
			} catch {
				helpTextContent.value = ""
			} finally {
				loading.value = false
			}
		}
	},
	{ immediate: true }
)

function onClose(value: boolean) {
	isEditing.value = false
	emit("update:visible", value)
}

function cancelEdit() {
	if (props.readonly) {
		isEditing.value = false
	} else {
		emit("update:visible", false)
	}
}

async function save() {
	saving.value = true
	try {
		helpTextContent.value = collapseEmptyParagraphs(helpTextContent.value)
		const request = new UpdateHelpTextRequest({ helpText: helpTextContent.value })
		await helpLabelClient.helptextPut(props.helpElementName, request)
		const hasHelpText = helpTextContent.value !== null && helpTextContent.value !== ""
		helpLabelStore.updateHelpText(props.helpElementName, hasHelpText)
		toast.add({ severity: "success", summary: "Help text saved", life: 2000 })
		isEditing.value = false
		emit("saved")
	} catch (err: any) {
		toast.add({ severity: "error", summary: "Error saving help text", detail: err?.message, life: 4000 })
	} finally {
		saving.value = false
	}
}
</script>

<style scoped>
.header-btn {
	cursor: pointer;
	color: #6b7280;
	font-size: 1rem;
	padding: 0.25rem;
	border: none;
	background: none;
}

.header-btn:hover {
	color: #374151;
}
</style>
